import DeploymentStageValues from "./DeploymentStageValues";
import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Checkbox, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, LinearProgress, ListItemText, MenuItem, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography, useMediaQuery, useTheme } from "@mui/material";
import { MonitorDialog } from "./MonitorControls";
import { STAGES, stageLabel } from "../utils/stages";
import { targetKeyOf } from "../utils/stageTargets";
import "./PromotionReportDialog.css";

const DEFAULT_LANE = "";
const PAGE_SIZES = [50, 100, 200];
const validId = value => typeof value === "string" && value.trim().length > 0;
const groupsOf = item => Array.isArray(item?.stagePackages) ? item.stagePackages : [item];

/** Quellen des Reports: Standard-Transportweg (nur mit Paketen) und alle Topics. */
export function reportSources(topics = [], defaultPackages = []) {
  const sources = [];
  if (defaultPackages.length) sources.push({ id: DEFAULT_LANE, name: null, packages: defaultPackages });
  for (const topic of topics) sources.push({ id: topic.id, name: topic.name, archived: !!topic.archived, packages: Array.isArray(topic.stagePackages) ? topic.stagePackages : [] });
  return sources;
}

/** Stages, für die die Quelle Paketstände hat, in Landschaftsreihenfolge. */
export function reportStages(packages = []) {
  const present = new Set(packages.flatMap(item => groupsOf(item).map(group => group?.sourceStage)));
  return STAGES.filter(stage => present.has(stage.key)).map(stage => stage.key);
}

/** Eine Zeile je Artefakt und Paketstand; Werte wie in PromotionArtifactDeployments. */
export function reportRows(packages = [], stageKeys = []) {
  const rows = [];
  for (const item of packages) {
    for (const group of groupsOf(item)) {
      if (!group || !stageKeys.includes(group.sourceStage)) continue;
      const records = Array.isArray(group.artifacts) ? group.artifacts : [];
      const byId = new Map(records.filter(record => validId(record?.id)).map(record => [record.id, record]));
      const ids = [...new Set([...(Array.isArray(group.artifactIds) ? group.artifactIds : []), ...byId.keys()].filter(validId))];
      // Pakettransport (Welle 6): Kennzeichen des Paketstands, nur "package" wird übernommen.
      const transport = group.transport === "package" || item.transport === "package" ? { transport: "package" } : {};
      for (const id of ids) {
        const record = byId.get(id);
        rows.push({ packageId: item.packageId, packageName: item.packageName || item.packageId, stage: group.sourceStage,
          plant: group.plant || item.plant || null, id, name: validId(record?.name) ? record.name : null,
          ci: record?.deployment?.ci, edge: record?.deployment?.edgeManual, deploymentByStage: record?.deploymentByStage,
          // Mehrzielmodus: Zeile je Ziel einer Stufe (Gruppe trägt targetId und targetName).
          ...(group.targetId ? { targetId: group.targetId, targetName: group.targetName || null } : {}), ...transport });
      }
    }
  }
  const order = key => STAGES.findIndex(stage => stage.key === key);
  return rows.sort((a, b) => order(a.stage) - order(b.stage) || a.packageName.localeCompare(b.packageName)
    || (a.targetName || "").localeCompare(b.targetName || "") || (a.name || a.id).localeCompare(b.name || b.id));
}

/**
 * Stände für den Filter „Instanz“: je Stufe eine Instanz, im Mehrzielmodus je Ziel einer Stufe.
 * `{ key, stage, targetId? }`; `key` ist der Stufenschlüssel bzw. der targetKey.
 */
export function reportStands(packages = []) {
  const targets = new Map();
  for (const item of packages) for (const group of groupsOf(item)) {
    if (!group?.targetId) continue;
    const list = targets.get(group.sourceStage) || new Map();
    list.set(group.targetId, group.targetName || null);
    targets.set(group.sourceStage, list);
  }
  return reportStages(packages).flatMap(stage => targets.has(stage)
    ? [...targets.get(stage)].map(([targetId, name]) => ({ key: targetKeyOf(stage, targetId), stage, targetId, name }))
    : [{ key: stage, stage }]);
}

// Deploymentarten des Reportfilters: Feld im Deploymentplan je Zielstufe und im gespeicherten Auftrag.
export const DEPLOY_KINDS = Object.freeze({ cloud: { plan: "ci", row: "ci" }, edge: { plan: "edgeManual", row: "edge" } });
/** Artefakt mit geplantem Deployment dieser Art: auf mindestens einer Zielstufe, sonst laut gespeichertem Auftrag. */
export function rowHasDeployment(row, kind) {
  const fields = DEPLOY_KINDS[kind];
  if (!fields) return false;
  return Array.isArray(row?.deploymentByStage) && row.deploymentByStage.length
    ? row.deploymentByStage.some(plan => plan?.[fields.plan] === true) : row?.[fields.row] === true;
}

export default function PromotionReportDialog({ topics = [], defaultPackages = [], stages = [], initialTopicId = DEFAULT_LANE, onClose, loadPackages, defaultSourceLabel, className = "", note = null }) {
  const { t } = useTranslation(["freigabe", "gemeinsam"]);
  const id = useId();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const sources = useMemo(() => reportSources(topics, defaultPackages), [topics, defaultPackages]);
  const [sourceId, setSourceId] = useState(() => sources.some(source => source.id === initialTopicId) ? initialTopicId : (sources[0]?.id ?? DEFAULT_LANE));
  const source = sources.find(item => item.id === sourceId);
  const [loaded, setLoaded] = useState({ sourceId: null, packages: [], loading: false, error: null });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!loadPackages || !source) return undefined;
    const controller = new AbortController();
    let live = true;
    setLoaded({ sourceId, packages: [], loading: true, error: null });
    Promise.resolve().then(() => loadPackages(sourceId, controller.signal)).then(packages => {
      if (live) setLoaded({ sourceId, packages, loading: false, error: null });
    }).catch(error => {
      if (live) setLoaded({ sourceId, packages: [], loading: false, error });
    });
    return () => { live = false; controller.abort(); };
  }, [loadPackages, source, sourceId, attempt]);
  const loading = !!loadPackages && !!source && (loaded.sourceId !== sourceId || loaded.loading);
  const readError = loadPackages && loaded.sourceId === sourceId ? loaded.error : null;
  const packages = loadPackages ? (loaded.sourceId === sourceId ? loaded.packages : []) : source?.packages;
  const stands = useMemo(() => reportStands(packages), [packages]);
  const available = useMemo(() => stands.map(item => item.key), [stands]);
  // Filter „Instanz“: der Paketstand einer Instanz oder alle; gilt je Quelle und fällt beim Quellwechsel auf „Alle“ zurück.
  const [standFilter, setStandFilter] = useState({ sourceId: null, stage: "" });
  const stand = standFilter.sourceId === sourceId && available.includes(standFilter.stage) ? standFilter.stage : "";
  const standEntry = stands.find(item => item.key === stand);
  const chosen = useMemo(() => reportStages(packages).filter(key => !standEntry || key === standEntry.stage), [packages, standEntry]);
  // Filter „Deploymentart“: Cloud, Edge oder beide (mindestens eine). Beide zeigen alle Artefakte, eine nur Artefakte
  // mit geplantem Deployment dieser Art.
  const [kinds, setKinds] = useState(["cloud", "edge"]);
  const [paging, setPaging] = useState({ page: 0, rows: PAGE_SIZES[0] });
  const rows = useMemo(() => reportRows(packages, chosen)
    .filter(row => !standEntry?.targetId || row.targetId === standEntry.targetId)
    .filter(row => kinds.length === Object.keys(DEPLOY_KINDS).length || kinds.some(kind => rowHasDeployment(row, kind))), [packages, chosen, standEntry, kinds]);
  const hasPlant = rows.some(row => row.plant);
  const packageCount = new Set(rows.map(row => row.packageId)).size;
  const stageCount = new Set(rows.map(row => `${row.stage}@${row.targetId || ""}`)).size;
  const page = Math.min(paging.page, Math.max(0, Math.ceil(rows.length / paging.rows) - 1));
  const visible = rows.slice(page * paging.rows, (page + 1) * paging.rows);
  const instanceOf = key => stages.find(stage => stage.key === key)?.instanceName;
  const stageName = key => instanceOf(key) ? `${stageLabel(key)} · ${instanceOf(key)}` : stageLabel(key);
  // Im Mehrzielmodus trägt der Stand den Namen seines Ziels.
  const standName = item => item.targetId ? `${stageLabel(item.stage)} · ${item.name || item.targetId}` : stageName(item.key);
  const sourceName = source => source.id === DEFAULT_LANE ? (defaultSourceLabel || t("topics.defaultLane")) : `${source.name}${source.archived ? ` (${t("topics.archived")})` : ""}`;
  const firstPage = () => setPaging(previous => ({ ...previous, page: 0 }));
  const changeStand = value => { setStandFilter({ sourceId, stage: value }); firstPage(); };
  const kindLabel = kind => t(kind === "edge" ? "report.deployEdge" : "report.deployCloud");
  const changeKinds = value => {
    const next = (typeof value === "string" ? value.split(",") : value).filter(kind => DEPLOY_KINDS[kind]);
    if (!next.length) return;   // Mindestens eine Deploymentart bleibt gewählt.
    setKinds(Object.keys(DEPLOY_KINDS).filter(kind => next.includes(kind))); firstPage();
  };
  const changeSource = value => { setSourceId(value); setPaging(previous => ({ ...previous, page: 0 })); };
  return <MonitorDialog className={`promotion-dialog promotion-report-dialog ${className}`} maxWidth="lg" fullScreen={mobile} scroll="paper" aria-labelledby={`${id}-title`} onClose={onClose}>
    <DialogTitle id={`${id}-title`}>{t("report.title", { name: source ? (source.id === DEFAULT_LANE ? (defaultSourceLabel || t("topics.defaultLane")) : source.name) : t("report.button") })}</DialogTitle>
    <DialogContent className="promotion-report-body">
      {!sources.length ? <Typography className="promotion-report-empty">{t("report.noSource")}</Typography> : <>
        <div className="promotion-report-controls">
          <FormControl size="small" className="promotion-report-source">
            <InputLabel shrink id={`${id}-source`}>{t("report.topic")}</InputLabel>
            <Select displayEmpty labelId={`${id}-source`} label={t("report.topic")} value={sourceId} onChange={event => changeSource(event.target.value)}
              MenuProps={{ PaperProps: { className: "iio iio-monitor-menu" } }}>
              {sources.map(item => <MenuItem key={item.id || "default"} value={item.id}>{sourceName(item)}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" className="promotion-report-source">
            <InputLabel shrink id={`${id}-stand`}>{t("report.standFilter")}</InputLabel>
            <Select displayEmpty labelId={`${id}-stand`} label={t("report.standFilter")} value={stand} onChange={event => changeStand(event.target.value)}
              MenuProps={{ PaperProps: { className: "iio iio-monitor-menu" } }}>
              <MenuItem value="">{t("report.allStands")}</MenuItem>
              {stands.map(item => <MenuItem key={item.key} value={item.key}>{standName(item)}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" className="promotion-report-source">
            <InputLabel shrink id={`${id}-kind`}>{t("report.deployFilter")}</InputLabel>
            <Select multiple labelId={`${id}-kind`} label={t("report.deployFilter")} value={kinds} onChange={event => changeKinds(event.target.value)}
              renderValue={selected => selected.map(kindLabel).join(", ")} MenuProps={{ PaperProps: { className: "iio iio-monitor-menu promotion-report-menu" } }}>
              {Object.keys(DEPLOY_KINDS).map(kind => <MenuItem key={kind} value={kind}>
                <Checkbox size="small" checked={kinds.includes(kind)} disabled={kinds.length === 1 && kinds.includes(kind)}/>
                <ListItemText primary={kindLabel(kind)}/>
              </MenuItem>)}
            </Select>
          </FormControl>
        </div>
        {loading && <LinearProgress aria-label={t("report.loading")}/>}
        {readError && <Alert severity="error" action={<Button onClick={() => setAttempt(value => value + 1)}>{t("stageBranches.details.retry")}</Button>}>
          {t("report.readError")}
        </Alert>}
        {!loading && !readError && <>
        <div className="promotion-report-summary">
          <Typography component="p" className="promotion-report-count">{t("report.summary", { artifacts: t("promotion.artifactCount", { count: rows.length }),
            packages: t("report.packageCount", { count: packageCount }), stages: t("report.stageCount", { count: stageCount }) })}</Typography>
          <Typography component="p" className="promotion-muted">{t("deployment.intentHint")}</Typography>
          {rows.some(row => row.deploymentByStage?.length) && <Typography component="p" className="promotion-muted">{t("report.stagePlanHint")}</Typography>}
          {note && <Typography component="p" className="promotion-muted">{note}</Typography>}
        </div>
        {!rows.length ? <Typography className="promotion-report-empty">{t("report.empty")}</Typography> : <>
          <TableContainer className="promotion-report-table-scroll" tabIndex={0}>
            <Table stickyHeader size="small" className="promotion-report-table" aria-label={t("report.button")}>
              <TableHead><TableRow>
                <TableCell scope="col">{t("report.package")}</TableCell><TableCell scope="col">{t("report.stage")}</TableCell>
                <TableCell scope="col">{t("deployment.name")}</TableCell><TableCell scope="col">{t("deployment.id")}</TableCell>
                <TableCell scope="col">{t("deployment.ci")}</TableCell><TableCell scope="col">{t("deployment.edge")}</TableCell>
                {hasPlant && <TableCell scope="col">{t("report.plant")}</TableCell>}
              </TableRow></TableHead>
              <TableBody>{visible.map((row, index) => {
                const first = index === 0 || visible[index - 1].packageId !== row.packageId || visible[index - 1].stage !== row.stage
                  || visible[index - 1].targetId !== row.targetId;
                return <TableRow key={`${row.packageId}-${row.stage}-${row.targetId || ""}-${row.id}`} data-package={row.packageId}>
                  <TableCell component="th" scope="row" className={first ? undefined : "is-continuation"}>
                    <span className={first ? undefined : "promotion-visually-hidden"}>{row.packageName}</span>
                    {first && row.packageName !== row.packageId && <span className="promotion-report-package-id">{row.packageId}</span>}
                    {first && row.transport === "package" && <span className="promotion-report-package-id">{t("promotion.wholePackage")}</span>}
                  </TableCell>
                  <TableCell>{row.targetName ? `${stageLabel(row.stage)} · ${row.targetName}` : stageLabel(row.stage)}</TableCell>
                  <TableCell>{row.name || t("deployment.unknown")}</TableCell>
                  <TableCell>{row.id}</TableCell>
                  <TableCell><DeploymentStageValues plans={row.deploymentByStage} field="ci" fallback={row.ci}/></TableCell>
                  <TableCell><DeploymentStageValues plans={row.deploymentByStage} field="edgeManual" fallback={row.edge}/></TableCell>
                  {hasPlant && <TableCell>{row.plant || ""}</TableCell>}
                </TableRow>;
              })}</TableBody>
            </Table>
          </TableContainer>
          {rows.length > PAGE_SIZES[0] && <TablePagination component="div" className="promotion-pagination" count={rows.length} page={page}
            rowsPerPage={paging.rows} rowsPerPageOptions={PAGE_SIZES}
            onPageChange={(_, value) => setPaging(previous => ({ ...previous, page: value }))}
            onRowsPerPageChange={event => setPaging({ page: 0, rows: Number(event.target.value) })}
            labelRowsPerPage={t("report.rowsPerPage")} labelDisplayedRows={({ from, to, count }) => t("topics.pageRange", { from, to, count })}
            getItemAriaLabel={type => t(`topics.page.${type}`)}/>}
        </>}
        </>}
      </>}
    </DialogContent>
    <DialogActions><Button className="promotion-head-button" variant="outlined" onClick={onClose}>{t("gemeinsam:aktion.schliessen")}</Button></DialogActions>
  </MonitorDialog>;
}
